import { createRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  FormControlLabel,
  RadioGroup,
  Radio,
  ClickAwayListener,
} from '@mui/material'

import { Close, Search } from '@mui/icons-material'

type Props = {
  filterType: string
  filterValue: string
  handleSetActiveSearch: (event: React.MouseEvent<HTMLButtonElement>) => void
  handleChangeSearchField: (value: string) => void
  handleChangeFilterType: (filterType: string) => void
  handleEnterSearch: () => void
}

const InputSearchFieldComponent = ({
  filterType,
  filterValue,
  handleSetActiveSearch,
  handleChangeSearchField,
  handleChangeFilterType,
  handleEnterSearch,
}: Props) => {
  const { t } = useTranslation()

  const [openOptions, setOpenOptions] = useState(false)

  const inputRef = createRef<HTMLInputElement>()

  const searchOptions = ['playerId', 'username', 'email']

  const handleMenuOpen = (open: boolean) => {
    setOpenOptions(open)
  }

  return (
    <ClickAwayListener onClickAway={() => handleMenuOpen(false)}>
      <Box flexShrink={0} width={330} position="relative" mr={2}>
        <Box
          mr={1}
          mt="4px"
          display="flex"
          alignItems="center"
          gap={2}
          position="relative"
        >
          <TextField
            size="small"
            id="searchUsername"
            label={t(`labels.${filterType}`)}
            autoComplete="off"
            fullWidth
            margin="normal"
            variant="outlined"
            value={filterValue}
            onChange={(e: React.ChangeEvent<{ value: string | number }>) => {
              handleChangeSearchField(e.target.value as string)
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleEnterSearch()
                setOpenOptions(false)
              }
            }}
            inputRef={inputRef}
            InputLabelProps={{ shrink: true }}
            sx={{ m: 0, p: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="inherit" />
                </InputAdornment>
              ),
              inputProps: {
                maxLength: 100,
              },
            }}
            onFocus={() => handleMenuOpen(true)}
          />
          <IconButton onClick={handleSetActiveSearch} size="small">
            <Close fontSize="inherit" />
          </IconButton>
        </Box>
        <Box
          display={openOptions ? 'block' : 'none'}
          position="absolute"
          width="100%"
          minWidth={200}
          mt={1}
          py={2}
          px={3}
          component={Paper}
          borderRadius={2}
        >
          <RadioGroup
            row
            name="playerFilter"
            value={filterType}
            onChange={(event) => {
              if (inputRef.current) {
                inputRef.current.focus()
              }
              handleChangeFilterType(event.target.value)
            }}
            sx={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridTemplateRows: 'repeat(2, 1fr)',
            }}
          >
            {searchOptions.map((filter) => (
              <FormControlLabel
                key={filter}
                value={filter}
                control={<Radio color="primary" size="small" />}
                label={t(`labels.${filter}`)}
                labelPlacement="end"
                data-testid={filter}
                sx={{
                  '& .MuiTypography-root': {
                    fontSize: 14,
                  },
                  m: 0,
                }}
              />
            ))}
          </RadioGroup>
        </Box>
      </Box>
    </ClickAwayListener>
  )
}

export default InputSearchFieldComponent
